import { useWindowVirtualizer } from "@tanstack/react-virtual";
import moment from "moment/min/moment-with-locales";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { useCompetitorMarketTrendModal } from "../components/CompetitorMarketTrend";
import DataDescription from "../components/DataDescription";
import EmptyRow from "../components/EmptyRow";
import { getRadioName, getTrendLabel, toAutoDecimals } from "../components/HelperFunctions";
import {
    useFetchData,
    useResolvedRadioNames,
    useSetDaysOnPerspectiveChange,
    useSetPerspectiveOnDaysChange,
    useSetDaypartOnChange,
} from "../components/HelperHooks";
import PageDesciption from "../components/PageDesciption";
import DatesPicker, { latest2Weeks, latestDay, latestMonth, latestQuarter, latestWeek } from "../components/Pickers/DatesPicker";
import MarketPicker from "../components/Pickers/MarketPicker";
import DaysPicker from "../components/Pickers/DaysPicker";
import HoursPicker from "../components/Pickers/HoursPicker";
import MinMaxSpinsPicker from "../components/Pickers/MinMaxSpinsPicker";
import MusicSelectionTypePicker from "../components/Pickers/MusicSelectionTypePicker";
import PerspectivePicker from "../components/Pickers/PerspectivePicker";
import { useUserFormState } from "../components/UserSelection";
import CompetitorChartHeader from "../components/competitorChart/CompetitorChartHeader";
import CompetitorChartRow from "../components/competitorChart/CompetitorChartRow";
import { DataContent, InputSelection, ModulePage, SearchWithDownloadRow, SoleDescriptionRow, VirtualizedWindowContainer } from "../components/pageSections";
import { useSongCatalogueModal } from "../components/songCatalogue/SongCatalogueModal";
import useSortableData from "../components/useSortableData";
import ReleaseYearChart from "../components/ReleaseYearChart";

const extraDefaults = { selectionType: "all", minSpins: 0, maxSpins: 0 };

export default function CompetitorChart() {
    const moduleName = useMemo(() => "competitorChart", []);
    const latestCalculatedDate = useMemo(() => moment(), []);

    const [formData, setFormData, saveFormData, loadFormData] = useUserFormState(moduleName, extraDefaults);
    const [loading, errorCode, data, fetchData] = useFetchData();

    const [submittedMarketName, setSubmittedMarketName] = useState("");
    const [filterData, setFilterData] = useState({ searchString: "" });
    const [resultCount, setResultCount] = useState(0);
    const { setCatalogueSong, SongCatalogueModalComponent } = useSongCatalogueModal();
    const { setTrendSong, CompetitorMarketTrendModalComponent } = useCompetitorMarketTrendModal();
    const resolvedRadios = useResolvedRadioNames();

    const tableContainerRef = useRef(null);
    const advancedInputRef = useRef(null);

    const predefinedRanges = useMemo(() => {
        const baseDate = latestCalculatedDate;
        if (formData.dateRange === undefined) {
            setFormData(current => ({ ...current, dateRange: latestWeek(baseDate, true) }));
        }
        return [latestDay(baseDate), latestWeek(baseDate), latest2Weeks(baseDate), latestMonth(baseDate), latestQuarter(baseDate)];
    }, [formData.dateRange, latestCalculatedDate, setFormData]);

    useSetPerspectiveOnDaysChange(setFormData, formData.days);
    useSetDaysOnPerspectiveChange(setFormData, formData.perspective);
    useSetDaypartOnChange(setFormData, formData.station, formData.days, formData.hourRange);

    async function handleFormSubmit(event) {
        event.preventDefault();
        advancedInputRef.current.closeAdvanced();
        await saveFormData();
        await saveFormData(formData.market[0].market_id);
        // input parameters
        const id = formData.station[0]?.radio_id ?? formData.market[0].market_id;
        const newQuery = formData.selectionType === "new" ? "new/" : "";
        const startDate = moment(formData.dateRange[0]).startOf("day").format("YYYY-MM-DD");
        const endDate = moment(formData.dateRange[1]).endOf("day").format("YYYY-MM-DD");
        const startTime = moment().hours(formData.hourRange[0]).minutes(0).seconds(0).format("HH:mm:ss");
        const endTime = moment()
            .hours(formData.hourRange[1] - 1)
            .minutes(59)
            .seconds(59)
            .format("HH:mm:ss");
        const limit = formData.selectionType === "top" ? 50 : 0;
        const url = `/charts/competitor/songs/${newQuery}?id=${id}&start_dt=${startDate}&end_dt=${endDate}&limit=${limit}&min_spins=${formData.minSpins}&max_spins=${formData.maxSpins}&start_time=${startTime}&end_time=${endTime}`;
        await fetchData(url, formData.days);
        setSubmittedMarketName(formData.market[0]?.market_name);
    }
    const songsMemo = useMemo(() => {
        if (data) {
            let filteredData = data.songs;
            setResultCount(filteredData.length);
            if (filterData.searchString) {
                const raw = filterData.searchString.trim().toLowerCase();

                const andParts = raw
                    .split("+")
                    .map(p => p.trim())
                    .filter(Boolean);

                filteredData = filteredData.filter(x => {
                    const artist = x.artist.toLowerCase();
                    const title = x.title.toLowerCase();
                    const year = x.release_year?.toString() ?? "";

                    return andParts.every(part => {
                        let orTerms = part
                            .split(",")
                            .map(p => p.trim())
                            .filter(Boolean);

                        return orTerms.some(term => {
                            if (term.startsWith('"') && term.endsWith('"')) {
                                const exact = term.slice(1, -1).trim();
                                const regex = new RegExp(`\\b${exact}\\b`, "i");
                                return regex.test(artist) || regex.test(title) || year === exact;
                            }

                            return artist.includes(term) || title.includes(term) || year.startsWith(term);
                        });
                    });
                });
            }

            return filteredData;
        }
    }, [data, filterData.searchString]);
    const { sortedItems, requestSort, sortConfig } = useSortableData(songsMemo, { key: "spins", direction: "descending" });

    useMemo(() => {
        if (sortedItems) {
            setResultCount(sortedItems.length);
        }
    }, [sortedItems]);

    const csvHeaders = useMemo(() => {
        if (!data) return;
        return [
            ...["Artist", "Title", "Release Year", "Market Spins", "Avg. Spins", "Item ID"],
            ...data.radio_ids.map(x => [getRadioName(x, resolvedRadios) + " Spins", getRadioName(x, resolvedRadios) + " Trend"]).flatMap(x => x),
        ];
    }, [data, resolvedRadios]);

    const csvData = useMemo(() => {
        if (!sortedItems) return;
        return sortedItems.map(x => [
            ...[x.artist, x.title, x.release_year, x.spins, toAutoDecimals(x.average_spins, 0), x.item_id],
            ...data.radio_ids.map((_, i) => [x.radio_spins[i]?.spins, getTrendLabel(x.radio_spins[i]?.trend)]).flatMap(x => x),
        ]);
    }, [data, sortedItems]);

    const virtualizer = useWindowVirtualizer({
        count: sortedItems?.length || 1,
        estimateSize: useCallback(() => 40, []),
        getItemKey: useCallback(i => sortedItems?.length && sortedItems[i].item_id, [sortedItems]),
        overscan: 30,
        scrollMargin: tableContainerRef.current?.offsetTop ?? 0,
    });

    const memoizedChart = useMemo(() => {
        if (!data || !formData.dateRange) return null;
        const endYear = moment(data.end_date).format("YYYY");

        return (
            <ReleaseYearChart
                fullData={data}
                data={data.songs}
                endYear={endYear}
                loading={loading}
                error={errorCode}
                submittedMarketName={submittedMarketName}
                moduleName={moduleName}
                isCompetitor={true}
            />
        );
    }, [data, submittedMarketName, loading, errorCode]);

    return (
        <ModulePage>
            <ModulePage.Title>Competitor Chart</ModulePage.Title>
            <ModulePage.InputSelection
                handleFormSubmit={handleFormSubmit}
                disabled={!formData.market.length || !formData.dateRange || !formData.days.length || !formData.hourRange}>
                <InputSelection.Main>
                    <MarketPicker
                        formData={formData}
                        setFormData={setFormData}
                        name={moduleName}
                        filterFunction={x => x.type === "competitor"}
                        disabled={formData.station?.length}
                        loadOnChange={loadFormData}
                    />
                    <DatesPicker formData={formData} setFormData={setFormData} predefinedRanges={predefinedRanges} latestAvailableDate={latestCalculatedDate} />
                    <MusicSelectionTypePicker formData={formData} setFormData={setFormData} name={moduleName} />
                </InputSelection.Main>
                <InputSelection.Advanced moduleName={moduleName} ref={advancedInputRef}>
                    <DaysPicker formData={formData} setFormData={setFormData} name={moduleName} />
                    <HoursPicker formData={formData} setFormData={setFormData} name={moduleName} />
                    <PerspectivePicker formData={formData} setFormData={setFormData} name={moduleName} />
                    <MinMaxSpinsPicker filterData={formData} setFilterData={setFormData} name={moduleName} />
                </InputSelection.Advanced>
            </ModulePage.InputSelection>
            <ModulePage.DataContent loading={loading} errorCode={errorCode}>
                <DataContent.Placeholder show={!data}>
                    <PageDesciption
                        title={"Competitor Chart"}
                        whatList={[
                            "Compare your own rotations to your main competitors",
                            "Filter by specific days or dayparts, or by type of song – new music, top 50, all songs",
                            "Clicking on “Trend” will show you a breakdown of the rotation development for all stations",
                        ]}
                        knowList={[
                            {
                                text: "Rotations are calculated based on actual spins by each station, accounting for total number of spins and size of rotations",
                            },
                            {
                                text: "The “trend arrow” next to the number of spins is based on the most recent 3 weeks",
                            },
                            {
                                text: "Competitor chart is a great place to “look for holes” to find popular songs among your competitors that you are missing out on",
                            },
                        ]}
                        relatedLinks={[
                            {
                                title: "Efficient Market Surveillance",
                                url: "https://radioanalyzer.com/blog/finally-efficient-market-surveillance",
                            },
                        ]}
                        videoSrc="https://www.youtube.com/embed/twR94nuVHUM?si=KPHLbtmWe36H3wrX"
                    />
                </DataContent.Placeholder>
                {data && (
                    <>
                        {data && <>{memoizedChart}</>}
                        <SoleDescriptionRow>
                            <h4 className="fw-bold text-uppercase mb-1 d-flex column-gap-1">Competitor Chart</h4>
                            <DataDescription>
                                <DataDescription.Simple label="Market" value={submittedMarketName} />
                                <DataDescription.DateRange startDate={data?.start_date} endDate={data?.end_date} />
                                <DataDescription.TimeRange startTime={data?.start_time} endTime={data?.end_time} />
                                <DataDescription.Perspective days={data?.days} />
                                {(data?.min_spins > 0 || data?.max_spins > 0) && (
                                    <DataDescription.Simple label="Spins" value={`${data.min_spins} - ${data.max_spins || "∞"}`} />
                                )}
                                <DataDescription.MarketRadios marketRadioIds={data?.radio_ids} />
                                <DataDescription.Simple label={"Results"} value={resultCount} />
                            </DataDescription>
                        </SoleDescriptionRow>
                        <SearchWithDownloadRow
                            filterData={filterData}
                            setFilterData={setFilterData}
                            csvHeaders={csvHeaders}
                            data={csvData}
                            filename={`Chart - ${submittedMarketName}`}
                        />

                        <CompetitorChartHeader sortConfig={sortConfig} requestSort={requestSort} radioIds={data.radio_ids} />
                        <VirtualizedWindowContainer containerRef={tableContainerRef} virtualizer={virtualizer}>
                            {virtualizer.getVirtualItems().map(virtualRow => {
                                const item = sortedItems[virtualRow.index];
                                if (!item) return <EmptyRow key={virtualRow.key} text="No songs" />;
                                return (
                                    <CompetitorChartRow
                                        item={item}
                                        key={virtualRow.key}
                                        virtualRow={virtualRow}
                                        virtualizer={virtualizer}
                                        radioIds={data.radio_ids}
                                        setCatalogueSong={setCatalogueSong}
                                        setTrendSong={setTrendSong}
                                    />
                                );
                            })}
                        </VirtualizedWindowContainer>
                    </>
                )}
                <CompetitorMarketTrendModalComponent marketId={data?.market_id} basisWeek={data?.end_date} />
                <SongCatalogueModalComponent />
            </ModulePage.DataContent>
        </ModulePage>
    );
}
